import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  createAdvanceSalaryBatch,
  deleteAdvanceSalaryBatch,
  fetchAdvanceSalaryFormMeta,
  groupAdvanceSalaryBatches,
  listAllSalaryAdvances,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceSalaryBatch,
  type AdvanceSalaryFormMeta,
} from "@/lib/hr/salaryAdvancesStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  Banknote,
  CalendarRange,
  Check,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Minus,
  Plus,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
const ADVANCES_FETCH_CAP = 500;

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

type AdvanceSortBy = "updated_desc" | "updated_asc" | "period_desc" | "period_asc";

type DraftLine = {
  clientId: string;
  advanceAmount: string;
  periodYear: number;
  periodMonth: number;
  reason: string;
};

type Draft = {
  employeeId: string;
  lines: DraftLine[];
};

function newClientId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}-${Math.random()}`;
}

function emptyLine(): DraftLine {
  return { clientId: newClientId(), advanceAmount: "", periodYear: 0, periodMonth: 0, reason: "" };
}

const emptyDraft = (employeeId = ""): Draft => ({
  employeeId,
  lines: [emptyLine()],
});

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function employeeLabel(employees: EmployeeRecord[], employeeId: string): string {
  const e = employees.find((x) => x.id === employeeId);
  if (!e) return employeeId || "—";
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeeBadge(employees: EmployeeRecord[], employeeId: string): { label: string; className: string } {
  const label = employeeLabel(employees, employeeId);
  const palettes = [
    "bg-sky-500/15 text-sky-700",
    "bg-emerald-500/15 text-emerald-700",
    "bg-violet-500/15 text-violet-700",
    "bg-amber-500/15 text-amber-700",
    "bg-rose-500/15 text-rose-700",
    "bg-teal-500/15 text-teal-700",
  ] as const;
  let h = 0;
  for (let i = 0; i < employeeId.length; i += 1) h = (h * 31 + employeeId.charCodeAt(i)) >>> 0;
  return { label, className: palettes[h % palettes.length] };
}

function buildPeriodOptions(
  meta: AdvanceSalaryFormMeta | null,
  selectedKeys: Set<string>,
  currentLineKey?: string,
): Array<{ year: number; month: number; key: string; label: string }> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const reserved = new Set((meta?.reservedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];

  // Current year Jan–Dec, then continue through the next calendar year (24 months from Jan).
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    if (blocked.has(key)) continue;
    if (reserved.has(key)) continue;
    if (selectedKeys.has(key) && key !== currentLineKey) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }

  return options;
}

function batchPeriodSummary(batch: AdvanceSalaryBatch): string {
  return batch.lines
    .map((line) => periodLabel(line.periodYear, line.periodMonth, MONTHS))
    .join(" · ");
}

function batchStatusSummary(batch: AdvanceSalaryBatch): string {
  const deducted = batch.lines.filter((l) => l.status === "deducted").length;
  if (deducted === batch.lines.length) return "All deducted";
  if (deducted > 0) return `${deducted}/${batch.lines.length} deducted`;
  return "Pending";
}

function SalaryPeriodPicker({
  open,
  onOpenChange,
  periodYear,
  periodMonth,
  periodOptions,
  pickerYear,
  onPickerYearChange,
  onSelect,
  disabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  periodYear: number;
  periodMonth: number;
  periodOptions: Array<{ year: number; month: number; key: string; label: string }>;
  pickerYear: number;
  onPickerYearChange: (year: number) => void;
  onSelect: (year: number, month: number) => void;
  disabled?: boolean;
}) {
  const availableKeys = new Set(periodOptions.map((opt) => opt.key));
  const minPickerYear = periodOptions[0]?.year ?? new Date().getFullYear();
  const maxPickerYear = periodOptions[periodOptions.length - 1]?.year ?? minPickerYear;
  const selectedLabel =
    periodYear && periodMonth ? periodLabel(periodYear, periodMonth, MONTHS) : null;

  return (
    <Popover modal={true} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "glass-input h-10 w-full justify-start text-left font-normal",
            !selectedLabel && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          <CalendarRange className="mr-2 h-4 w-4 shrink-0 opacity-70" />
          {selectedLabel ?? "Select period"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[min(100vw-2rem,20rem)] p-4" align="start" sideOffset={6}>
        <div className="mb-3 space-y-1">
          <p className="text-sm font-semibold text-foreground">Select salary period</p>
          <p className="text-xs text-muted-foreground">Select month and year for this advance.</p>
        </div>

        {periodOptions.length === 0 ? (
          <p className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center text-xs text-muted-foreground">
            No available salary periods for this employee.
          </p>
        ) : (
          <div className="rounded-lg border border-border/50 bg-muted/10 p-3">
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear <= minPickerYear}
                onClick={() => onPickerYearChange(Math.max(minPickerYear, pickerYear - 1))}
                aria-label="Previous year"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold tabular-nums text-foreground">{pickerYear}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={pickerYear >= maxPickerYear}
                onClick={() => onPickerYearChange(Math.min(maxPickerYear, pickerYear + 1))}
                aria-label="Next year"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {MONTH_LABELS.map((label, monthIndex) => {
                const month = monthIndex + 1;
                const key = periodKey(pickerYear, month);
                const isAvailable = availableKeys.has(key);
                const isSelected = periodYear === pickerYear && periodMonth === month;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={!isAvailable}
                    onClick={() => onSelect(pickerYear, month)}
                    className={cn(
                      "h-10 rounded-md text-sm font-medium transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : isAvailable
                          ? "border border-border/50 bg-background text-foreground hover:bg-muted"
                          : "cursor-not-allowed border border-transparent bg-muted/30 text-muted-foreground/50",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Selected: <span className="font-medium text-foreground">{selectedLabel ?? "None"}</span>
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function SalaryAdvances() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_advance_can_create");
  const canDelete = can(user, "salary_advance_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [batches, setBatches] = useState<AdvanceSalaryBatch[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState<string>("all");
  const [sortBy, setSortBy] = useState<AdvanceSortBy>("updated_desc");
  const [quickDate, setQuickDate] = useState<"today" | "week" | "month" | "all">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);

  const selectedModalEmployee = useMemo(
    () => (draft.employeeId ? employees.find((e) => e.id === draft.employeeId) : undefined),
    [draft.employeeId, employees],
  );

  const [formMeta, setFormMeta] = useState<AdvanceSalaryFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdvanceSalaryBatch | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [periodPickerLineId, setPeriodPickerLineId] = useState<string | null>(null);
  const [periodPickerYear, setPeriodPickerYear] = useState(() => new Date().getFullYear());

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllSalaryAdvances({ page: 1, perPage: ADVANCES_FETCH_CAP });
      setBatches(groupAdvanceSalaryBatches(res.rows));
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setBatches([]);
        return;
      }
      toast.error(salaryAdvancesApiErrorMessage(e));
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!modalOpen || !draft.employeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchAdvanceSalaryFormMeta(draft.employeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(salaryAdvancesApiErrorMessage(e));
          setFormMeta(null);
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, draft.employeeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return batches.filter((batch) => {
      if (filterEmployee !== "all" && batch.employeeId !== filterEmployee) return false;
      if (quickDate !== "all") {
        const updated = parseISO(batch.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (!q) return true;
      const empName = employeeLabel(employees, batch.employeeId).toLowerCase();
      const hay = `${empName} ${batch.totalAmount} ${batchPeriodSummary(batch)} ${batch.lines.map((l) => l.reason ?? "").join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [batches, query, filterEmployee, quickDate, employees]);

  const sortedFiltered = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const aPeriod = a.lines[0] ? periodKey(a.lines[0].periodYear, a.lines[0].periodMonth) : "";
      const bPeriod = b.lines[0] ? periodKey(b.lines[0].periodYear, b.lines[0].periodMonth) : "";
      switch (sortBy) {
        case "updated_asc":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "period_desc":
          return bPeriod.localeCompare(aPeriod) || b.batchId.localeCompare(a.batchId);
        case "period_asc":
          return aPeriod.localeCompare(bPeriod) || a.batchId.localeCompare(b.batchId);
        case "updated_desc":
        default:
          return b.updatedAt.localeCompare(a.updatedAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = filterEmployee !== "all" ? 1 : 0;
  const resetFilters = () => setFilterEmployee("all");

  const paginationKey = `${query}|${filterEmployee}|${quickDate}|${sortBy}|${pageSize}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(sortedFiltered, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    if (!employees.length) {
      toast.error("No employees available.");
      return;
    }
    setDraft(emptyDraft(employees[0]?.id ?? ""));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setDraft(emptyDraft());
    setFormMeta(null);
    setPeriodPickerLineId(null);
  };

  const selectedPeriodKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const line of draft.lines) {
      if (line.periodYear && line.periodMonth) {
        keys.add(periodKey(line.periodYear, line.periodMonth));
      }
    }
    return keys;
  }, [draft.lines]);

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.clientId === clientId ? { ...line, ...patch } : line)),
    }));
  };

  const addLine = () => {
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyLine()] }));
  };

  const removeLine = (clientId: string) => {
    setDraft((d) => {
      if (d.lines.length <= 1) return d;
      return { ...d, lines: d.lines.filter((line) => line.clientId !== clientId) };
    });
  };

  const save = async () => {
    if (saving) return;
    if (!draft.employeeId) return toast.error("Select an employee.");
    if (!formMeta) return toast.error("Employee salary details are still loading.");

    const payloadLines: Array<{ advanceAmount: number; periodYear: number; periodMonth: number; reason: string | null }> = [];
    const seen = new Set<string>();

    for (const line of draft.lines) {
      const amount = safeNumber(line.advanceAmount);
      if (!amount || amount <= 0) return toast.error("Each advance amount must be greater than zero.");
      if (!line.periodYear || !line.periodMonth) return toast.error("Select a salary period for each advance line.");
      if (amount > formMeta.grossSalary) {
        return toast.error(`Advance amount cannot exceed gross salary (${formatAmountCommas(String(formMeta.grossSalary))}).`);
      }
      const key = periodKey(line.periodYear, line.periodMonth);
      if (seen.has(key)) return toast.error("Each salary period can only be used once.");
      seen.add(key);
      payloadLines.push({
        advanceAmount: amount,
        periodYear: line.periodYear,
        periodMonth: line.periodMonth,
        reason: line.reason.trim() || null,
      });
    }

    setSaving(true);
    try {
      const result = await createAdvanceSalaryBatch({ employeeId: draft.employeeId, lines: payloadLines });
      toast.success(result.message);
      closeModal();
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const result = await deleteAdvanceSalaryBatch({
        employeeId: deleteTarget.employeeId,
        batchId: deleteTarget.batchId,
      });
      toast.success(result.message);
      setDeleteTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Advance salary access required"
      message="You don’t have permission to view advance salary. Ask an administrator to grant salary_advance_can_view_any."
    />
  ) : (
    <Layout title="Advance salary">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Advance salary</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Record advance amounts by salary period — each line is deducted when that period&apos;s payroll is processed.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate} disabled={!employees.length}>
              <Plus className="w-4 h-4" />
              Add advance salary
            </Button>
          ) : null}
        </div>

        <Dialog open={modalOpen} onOpenChange={(o) => (o ? setModalOpen(true) : closeModal())}>
          <DialogContent className="max-w-5xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>New advance salary</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Employee</Label>
                <Popover
                  modal
                  open={employeePickerOpen}
                  onOpenChange={(next) => {
                    setEmployeePickerOpen(next);
                    if (next) setEmployeePickerCycle((c) => c + 1);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeePickerOpen}
                      className={cn(
                        "glass-input h-10 w-full justify-between px-3 font-normal",
                        !draft.employeeId && "text-muted-foreground",
                      )}
                    >
                      {selectedModalEmployee ? (
                        <span className="flex items-center gap-2 truncate">
                          <Avatar className="h-5 w-5 text-[8px]">
                            {selectedModalEmployee.photoDataUrl ? (
                              <AvatarImage src={selectedModalEmployee.photoDataUrl} alt={employeeDisplayName(selectedModalEmployee)} />
                            ) : null}
                            <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                              {employeeInitials(selectedModalEmployee)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate">{employeeLabel(employees, selectedModalEmployee.id)}</span>
                        </span>
                      ) : (
                        <span className="truncate text-left">Select employee…</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                    <Command key={employeePickerCycle} shouldFilter>
                      <CommandInput placeholder="Search name, code or department…" className="h-9" />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          {activeEmployees.map((e) => {
                            const label = employeeLabel(employees, e.id);
                            const subtitle = employeeSubtitle(e);
                            const selected = draft.employeeId === e.id;
                            const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <CommandItem
                                key={e.id}
                                value={searchValue}
                                onSelect={() => {
                                  setDraft(emptyDraft(e.id));
                                  setEmployeePickerOpen(false);
                                }}
                                className="gap-2"
                              >
                                <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                  {e.photoDataUrl ? (
                                    <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                  ) : null}
                                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                    {employeeInitials(e)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate text-sm font-medium">{label}</span>
                                  {subtitle && (
                                    <span className="truncate text-[11px] opacity-60 leading-tight">
                                      {subtitle}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {draft.employeeId ? (
                <div className="rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  {metaLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading gross salary and available periods…
                    </span>
                  ) : formMeta ? (
                    <>
                      Gross salary limit:{" "}
                      <span className="font-semibold text-foreground">{formatAmountCommas(String(formMeta.grossSalary))}</span>
                      {" · "}
                      Periods with finalized or paid payroll are excluded from selection.
                    </>
                  ) : (
                    "Could not load employee salary details."
                  )}
                </div>
              ) : null}

              {draft.employeeId ? (
                <div className="space-y-3">
                  <div className="hidden md:grid md:grid-cols-[minmax(9rem,1fr)_minmax(11rem,1.1fr)_minmax(12rem,1.4fr)_auto] gap-3 px-1">
                    <Label className="text-xs text-muted-foreground">Advance amount</Label>
                    <Label className="text-xs text-muted-foreground">Salary period</Label>
                    <Label className="text-xs text-muted-foreground">Reason</Label>
                    <span className="sr-only">Actions</span>
                  </div>

                  {draft.lines.map((line, index) => {
                    const lineKey =
                      line.periodYear && line.periodMonth ? periodKey(line.periodYear, line.periodMonth) : undefined;
                    const periodOptions = buildPeriodOptions(formMeta, selectedPeriodKeys, lineKey);

                    return (
                      <div
                        key={line.clientId}
                        className="grid grid-cols-1 md:grid-cols-[minmax(9rem,1fr)_minmax(11rem,1.1fr)_minmax(12rem,1.4fr)_auto] gap-3 items-end rounded-lg border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none"
                      >
                        <div className="space-y-2">
                          {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Advance amount</Label> : null}
                          <Input
                            className="glass-input h-10"
                            value={line.advanceAmount}
                            onChange={(e) => updateLine(line.clientId, { advanceAmount: formatMoney(e.target.value) })}
                            placeholder="e.g. 10,000"
                            inputMode="decimal"
                          />
                        </div>
                        <div className="space-y-2">
                          {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Salary period</Label> : null}
                          <SalaryPeriodPicker
                            open={periodPickerLineId === line.clientId}
                            onOpenChange={(open) => {
                              if (open) {
                                setPeriodPickerLineId(line.clientId);
                                setPeriodPickerYear(
                                  line.periodYear || periodOptions[0]?.year || new Date().getFullYear(),
                                );
                              } else {
                                setPeriodPickerLineId((id) => (id === line.clientId ? null : id));
                              }
                            }}
                            periodYear={line.periodYear}
                            periodMonth={line.periodMonth}
                            periodOptions={periodOptions}
                            pickerYear={periodPickerYear}
                            onPickerYearChange={setPeriodPickerYear}
                            onSelect={(year, month) => {
                              updateLine(line.clientId, { periodYear: year, periodMonth: month });
                              setPeriodPickerLineId(null);
                            }}
                            disabled={metaLoading || !formMeta}
                          />
                        </div>
                        <div className="space-y-2">
                          {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Reason</Label> : null}
                          <Input
                            className="glass-input h-10"
                            value={line.reason}
                            onChange={(e) => updateLine(line.clientId, { reason: e.target.value })}
                            placeholder="Optional reason"
                          />
                        </div>
                        <div className="flex items-center gap-2 pb-0.5">
                          {index === draft.lines.length - 1 ? (
                            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={addLine} aria-label="Add another period">
                              <Plus className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10 shrink-0 text-destructive"
                              onClick={() => removeLine(line.clientId)}
                              aria-label="Remove line"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" className="gap-2" onClick={() => void save()} disabled={saving || !draft.employeeId || metaLoading}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete advance salary?"
          description={
            deleteTarget
              ? `This will remove the advance salary record for ${employeeLabel(employees, deleteTarget.employeeId)} (${formatAmountCommas(String(deleteTarget.totalAmount))} across ${deleteTarget.lines.length} period${deleteTarget.lines.length === 1 ? "" : "s"}).`
              : ""
          }
          confirmLabel={deleting ? "Deleting..." : "Delete"}
          onConfirm={() => void confirmDelete()}
        />

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employee, amount, period…"
                />
              </div>
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter advance salary</SheetTitle>
                    <SheetDescription>Refine by employee.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All employees</SelectItem>
                          {activeEmployees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {employeeLabel(employees, e.id)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <SheetFooter className="mt-6 gap-2">
                    <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button type="button" variant="premium" className="w-full">
                        Apply filters
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect id="advance-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AdvanceSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="period_desc">Period: Newest first</SelectItem>
                    <SelectItem value="period_asc">Period: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button type="button" onClick={() => setViewMode("grid")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="Grid view">
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setViewMode("list")} className={cn("h-full px-3 text-sm transition-colors", viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50")} aria-label="List view">
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && sortedFiltered.length === 0 ? (
            <div className="text-sm text-muted-foreground">No advance salary records found for these filters.</div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginatedItems.map((batch, i) => (
                <motion.div key={batch.batchId} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <CatalogEntityCard
                    title={`Advance · ${formatAmountCommas(String(batch.totalAmount))}`}
                    statusBadge={employeeBadge(employees, batch.employeeId)}
                    primaryMeta={{ label: "Periods", value: batchPeriodSummary(batch), icon: CalendarRange, iconClassName: "text-sky-600" }}
                    secondaryMeta={{ label: "Status", value: batchStatusSummary(batch), icon: Banknote, iconClassName: "text-emerald-600" }}
                    footer={`${batch.lines.length} line${batch.lines.length === 1 ? "" : "s"} · Updated ${formatCatalogAuditDate(batch.updatedAt)}`}
                    onDelete={canDelete && batch.canDelete ? () => setDeleteTarget(batch) : undefined}
                  />
                  <div className="mt-2 px-1 space-y-1">
                    {batch.lines.map((line) => (
                      <div key={line.id} className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>
                          {periodLabel(line.periodYear, line.periodMonth, MONTHS)} · {formatAmountCommas(line.advanceAmount)}
                        </span>
                        {line.status === "deducted" ? <Badge variant="secondary">Deducted</Badge> : <Badge variant="outline">Pending</Badge>}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 && viewMode === "list" ? (
            <div className="space-y-3">
              {paginatedItems.map((batch, i) => (
                <motion.div key={batch.batchId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="h-14 w-16 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                      <Banknote className="w-7 h-7 text-emerald-500/70" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground truncate">
                        <span className={cn("mr-2 rounded-full px-2 py-0.5 text-[11px] font-semibold", employeeBadge(employees, batch.employeeId).className)}>
                          {employeeBadge(employees, batch.employeeId).label}
                        </span>
                        {formatAmountCommas(String(batch.totalAmount))}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{batchPeriodSummary(batch)}</p>
                      <div className="mt-2 space-y-1">
                        {batch.lines.map((line) => (
                          <p key={line.id} className="text-xs text-muted-foreground">
                            {periodLabel(line.periodYear, line.periodMonth, MONTHS)} · {formatAmountCommas(line.advanceAmount)}
                            {line.reason ? ` · ${line.reason}` : ""}
                            {line.status === "deducted" ? " · deducted" : " · pending"}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline">{batchStatusSummary(batch)}</Badge>
                      {canDelete && batch.canDelete ? (
                        <Button variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => setDeleteTarget(batch)}>
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : null}

          {!loading && sortedFiltered.length > 0 ? (
            <GridPaginationFooter page={page} totalPages={totalPages} totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} onPageChange={setPage} />
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
